import React, { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { WhiteDocumentIcon } from "../../KnowledgeBase/Icons";

const POPOVER_WIDTH = 350;
const VIEWPORT_MARGIN = 8;

// Rendered in a portal with fixed positioning (mirrors ActionMenu) so the
// summary card escapes the table's overflow-auto clipping and the sidebar's
// stacking context instead of sliding underneath it.
const SummaryPopover = ({ anchorEl, summary, popoverRef }) => {
  const [style, setStyle] = useState({ top: -9999, left: -9999, opacity: 0, pointerEvents: "none" });

  useLayoutEffect(() => {
    if (!anchorEl) return;

    const tableContainer = anchorEl.closest(".overflow-auto.custom-scrollbar");

    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      const containerRect = tableContainer ? tableContainer.getBoundingClientRect() : null;

      // Center under the button, then clamp so the card stays within the table
      // content area on the left — it can never slide under the sidebar — and
      // within the viewport on the right.
      const minLeft = containerRect ? containerRect.left : VIEWPORT_MARGIN;
      let left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
      left = Math.max(
        minLeft,
        Math.min(left, window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN)
      );

      // Hide while the anchor is scrolled under the sticky header or out of
      // the table viewport (same behaviour as ActionMenu).
      let isVisible = true;
      if (containerRect) {
        const headerOffset = 40;
        if (rect.top < containerRect.top + headerOffset || rect.bottom > containerRect.bottom) {
          isVisible = false;
        }
      }

      setStyle({
        top: rect.bottom + 8,
        left,
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? "auto" : "none",
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [anchorEl]);

  return createPortal(
    <div
      ref={popoverRef}
      style={{ ...style, position: "fixed", width: POPOVER_WIDTH }}
      className="z-[9999] border border-[#4285F4] bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.15)] overflow-hidden transition-opacity duration-75"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-[#4285F4] px-4 py-2.5 flex items-center gap-2">
        <WhiteDocumentIcon />
        <h3 className="text-white font-medium font-outfit text-[15px]">File Summary</h3>
      </div>
      <div className="px-3 py-1 max-h-[220px] overflow-y-auto custom-scrollbar">
        <p className="text-[#52525B] font-outfit text-[13px] leading-relaxed">{summary}</p>
      </div>
    </div>,
    document.body
  );
};

export default SummaryPopover;
